import type { PasteEditorLinkHandlers } from './viewer/modals/paste-editor-link-modal';
import type { AttachUploadLinkHandlers } from './viewer/tools/files/editorjs-attach-upload-link-tool';
import type { GalleryEditRequest } from './viewer/tools/gallery/editorjs-gallery-tool';
import type { AiInstructionsEditRequest } from './viewer/tunes/editorjs-ai-instructions-tune';

import { useApolloClient } from '@apollo/client/react';
import { Box } from '@chakra-ui/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { GET_UPLOADS_FOR_DOCUMENT } from '#graphql/queries/document-uploads';
import { DocumentAttachFileModal } from '#pages/document/files/document-attach-file-modal';
import {
  composerAttachmentMarkdownHref,
  type UploadFileResult,
} from '#pages/document/upload-image';

import { useDocumentChatComposer } from '../document-chat-composer-context';
import { useDocumentContext } from '../document-context';

import { useEditDocument } from './edit-document-context';
import { RenameDocumentModal } from './modals/rename-document-modal';
import { useRenameDocument } from './use-rename-document';
import { EditDocumentEditor } from './viewer/editor';
import { GridV1Editor, type GridV1EditorHandle } from './viewer/grid-v1-editor';
import { GridV1Toolbar, type GridV1EditorMode } from './viewer/grid-v1-toolbar';
import { MarkdownEditor } from './viewer/markdown-editor';
import { AiInstructionsModal } from './viewer/modals/ai-instructions-modal';
import { PasteEditorLinkModal } from './viewer/modals/paste-editor-link-modal';
import { GalleryEditModal } from './viewer/tools/gallery/gallery-edit-modal';
import { GalleryImageModalProvider } from './viewer/tools/gallery/gallery-image-modal-context';

interface ViewDocumentViewerProps {
  apiHost: string;
  userEmail: string;
  onCreateSubpage: () => void;
  /** Navigate to a document path (client-side). Used by Subpages block links. */
  onNavigateToDocument?: (path: string) => void;
  isRenameOpen: boolean;
  onRenameOpenChange: (open: boolean) => void;
}

export function ViewDocumentViewer({
  apiHost,
  userEmail,
  onCreateSubpage,
  onNavigateToDocument,
  isRenameOpen,
  onRenameOpenChange,
}: ViewDocumentViewerProps) {
  const {
    documentId,
    isNewDocument,
    isMarkdownDocument,
    isGridDocument,
    editorPlaceholderMuted,
    path,
    displayPath,
    editorValue,
    markdownValue,
    setMarkdownValue,
    gridDocumentValue,
    setGridDocumentValue,
    childrenItems,
  } = useDocumentContext();
  const { editorRef } = useEditDocument();
  const { documentPathForRename, getSerializedDocument, handleRename } =
    useRenameDocument();

  const openRename = useCallback(() => {
    onRenameOpenChange(true);
  }, [onRenameOpenChange]);

  const { appendEmphasis, focusComposer } = useDocumentChatComposer();
  const apolloClient = useApolloClient();
  const attachLinkSessionRef = useRef<AttachUploadLinkHandlers | null>(null);
  const attachUploadSucceededRef = useRef(false);
  const [attachModalOpen, setAttachModalOpen] = useState(false);
  const pasteEditorLinkSessionRef = useRef<PasteEditorLinkHandlers | null>(
    null,
  );
  const pasteEditorLinkSucceededRef = useRef(false);
  const [pasteEditorLinkModalOpen, setPasteEditorLinkModalOpen] =
    useState(false);
  const [galleryEditRequest, setGalleryEditRequest] =
    useState<GalleryEditRequest | null>(null);
  const [aiInstructionsRequest, setAiInstructionsRequest] =
    useState<AiInstructionsEditRequest | null>(null);
  const [gridMode, setGridMode] = useState<GridV1EditorMode>('view');
  const gridEditorRef = useRef<GridV1EditorHandle | null>(null);

  useEffect(() => {
    setGridMode('view');
  }, [documentId]);

  const handleRequestGalleryEdit = useCallback(
    (request: GalleryEditRequest) => {
      setGalleryEditRequest(request);
    },
    [],
  );

  const handleRequestAiInstructionsEdit = useCallback(
    (request: AiInstructionsEditRequest) => {
      setAiInstructionsRequest(request);
    },
    [],
  );

  const handleAddToChat = useCallback(
    (blockId: string) => {
      appendEmphasis(blockId);
      focusComposer();
    },
    [appendEmphasis, focusComposer],
  );

  const handleUpload = useCallback(
    async (file: File): Promise<string> => {
      const { uploadImageFile } = await import('../upload-image');
      const result = await uploadImageFile(file, {
        apiHost,
        userEmail,
        ...(documentId ? { documentId } : {}),
      });
      return result.url;
    },
    [apiHost, documentId, userEmail],
  );

  const handleRequestAttachUploadLink = useCallback(
    (handlers: AttachUploadLinkHandlers) => {
      attachUploadSucceededRef.current = false;
      attachLinkSessionRef.current = handlers;
      setAttachModalOpen(true);
    },
    [],
  );

  const handleAttachModalOpenChange = useCallback((open: boolean) => {
    if (!open) {
      if (!attachUploadSucceededRef.current) {
        attachLinkSessionRef.current?.cancel();
      }

      attachUploadSucceededRef.current = false;
      attachLinkSessionRef.current = null;
      setAttachModalOpen(false);
    }
  }, []);

  const handleAttachUploadResult = useCallback(
    (result: UploadFileResult) => {
      const href = composerAttachmentMarkdownHref(
        result.id,
        result.url,
        result.isEncrypted,
        apiHost,
      );
      attachUploadSucceededRef.current = true;
      attachLinkSessionRef.current?.applyHref(href);
      attachLinkSessionRef.current = null;
      setAttachModalOpen(false);
    },
    [apiHost],
  );

  const handleAttachUploadedRefetch = useCallback(() => {
    if (!documentId) return;

    void apolloClient.refetchQueries({ include: [GET_UPLOADS_FOR_DOCUMENT] });
  }, [apolloClient, documentId]);

  const handleRequestPasteEditorLink = useCallback(
    (handlers: PasteEditorLinkHandlers) => {
      pasteEditorLinkSucceededRef.current = false;
      pasteEditorLinkSessionRef.current = handlers;
      setPasteEditorLinkModalOpen(true);
    },
    [],
  );

  const handlePasteEditorLinkModalOpenChange = useCallback((open: boolean) => {
    if (!open) {
      if (!pasteEditorLinkSucceededRef.current) {
        pasteEditorLinkSessionRef.current?.cancel();
      }

      pasteEditorLinkSucceededRef.current = false;
      pasteEditorLinkSessionRef.current = null;
      setPasteEditorLinkModalOpen(false);
    }
  }, []);

  const handlePasteEditorLinkSubmit = useCallback(
    (href: string, label?: string) => {
      pasteEditorLinkSucceededRef.current = true;
      pasteEditorLinkSessionRef.current?.applyLink(href, label);
      pasteEditorLinkSessionRef.current = null;
      setPasteEditorLinkModalOpen(false);
    },
    [],
  );

  return (
    <GalleryImageModalProvider>
      <GalleryEditModal
        request={galleryEditRequest}
        onDismiss={() => setGalleryEditRequest(null)}
        onUpload={handleUpload}
      />
      <AiInstructionsModal
        request={aiInstructionsRequest}
        onDismiss={() => setAiInstructionsRequest(null)}
      />
      <DocumentAttachFileModal
        open={attachModalOpen}
        onOpenChange={handleAttachModalOpenChange}
        apiHost={apiHost}
        userEmail={userEmail}
        documentId={documentId}
        onUploaded={handleAttachUploadedRefetch}
        onUploadResult={handleAttachUploadResult}
      />
      <PasteEditorLinkModal
        open={pasteEditorLinkModalOpen}
        onOpenChange={handlePasteEditorLinkModalOpenChange}
        onSubmit={handlePasteEditorLinkSubmit}
      />
      {isMarkdownDocument ? (
        <MarkdownEditor
          documentId={documentId}
          apiHost={apiHost}
          userEmail={userEmail}
          value={markdownValue}
          onChange={setMarkdownValue}
          parentPath={displayPath}
          children={childrenItems}
          editorPlaceholderMuted={editorPlaceholderMuted}
          isNewDocument={isNewDocument}
          onCreateSubpage={onCreateSubpage}
          onNavigateToDocument={onNavigateToDocument}
          onRequestRename={openRename}
          onRequestGalleryEdit={handleRequestGalleryEdit}
          onUploaded={handleAttachUploadedRefetch}
        />
      ) : isGridDocument ? (
        <Box flex={1} minH={0} display="flex" flexDirection="column">
          <GridV1Toolbar
            mode={gridMode}
            onModeChange={setGridMode}
            onAddCell={() => gridEditorRef.current?.addCell()}
          />
          <GridV1Editor
            ref={gridEditorRef}
            mode={gridMode}
            value={gridDocumentValue}
            onChange={setGridDocumentValue}
            cellMarkdownEditorProps={{
              documentId,
              apiHost,
              userEmail,
              parentPath: displayPath,
              children: childrenItems,
              editorPlaceholderMuted,
              isNewDocument: false,
              onCreateSubpage,
              onNavigateToDocument,
              onRequestRename: openRename,
              onRequestGalleryEdit: handleRequestGalleryEdit,
              onUploaded: handleAttachUploadedRefetch,
            }}
          />
        </Box>
      ) : (
        <EditDocumentEditor
          key={documentId ?? path}
          ref={editorRef}
          value={editorValue}
          documentId={documentId}
          apiHost={apiHost}
          userEmail={userEmail}
          isNewDocument={isNewDocument}
          editorPlaceholderMuted={editorPlaceholderMuted}
          onUpload={handleUpload}
          parentPath={displayPath}
          children={childrenItems}
          onCreateSubpage={onCreateSubpage}
          onNavigateToDocument={onNavigateToDocument}
          onAddToChat={handleAddToChat}
          onRequestGalleryEdit={handleRequestGalleryEdit}
          onRequestAiInstructionsEdit={handleRequestAiInstructionsEdit}
          onRequestRename={openRename}
          onRequestAttachUploadLink={handleRequestAttachUploadLink}
          onRequestPasteEditorLink={handleRequestPasteEditorLink}
        />
      )}

      {!isNewDocument && (
        <RenameDocumentModal
          open={isRenameOpen}
          onOpenChange={onRenameOpenChange}
          documentPathForRename={documentPathForRename}
          getCurrentValue={getSerializedDocument}
          onRename={handleRename}
        />
      )}
    </GalleryImageModalProvider>
  );
}
